import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { BodyText, Icon, Switch, theme } from '@sg/garnish';

import { usePaymentBreakdown } from '../../../../hooks';

export const CreditPaymentMethod = (props: CreditPaymentMethodProps) => {
  const {
    isDisabled,
    shouldUseCredit,
    availableCredit,
    appliedCredit,
    onChangeCreditUsage,
    paymentBreakdown,
  } = props;

  const { creditAmount, formattedTotalCredit } = paymentBreakdown;
  const { applied, available } = useCreditUsageHint(appliedCredit, availableCredit);
  const { formatMessage } = useIntl();

  return (
    <View style={[styles.paymentMethod, styles.borderBottom]}>
      <View style={[styles.info, styles.infoText]}>
        <Icon name="IconCredit" color={theme.colors.SPINACH} width={32} height={32} />

        <View>
          <BodyText style={styles.spinachText} sizeMatch={['14']}>
            <FormattedMessage {...messages.creditUsageTitle} />
          </BodyText>

          <BodyText sizeMatch={['12']}>
            <FormattedMessage {...messages.creditUsageHint} values={{ applied, available }} />
          </BodyText>
        </View>
      </View>

      <View style={styles.info}>
        <Switch
          size="small"
          disabled={isDisabled}
          accessibilityLabel={formatMessage(messages.changeCreditUsage)}
          value={shouldUseCredit}
          onChange={onChangeCreditUsage}
        />

        <BodyText style={creditAmount > 0 ? styles.spinachText : undefined} sizeMatch={['14']}>
          {formattedTotalCredit}
        </BodyText>
      </View>
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const useCreditUsageHint = (appliedCredit: number, availableCredit: number) => {
  const { formatNumber } = useIntl();

  const available = formatNumber(Number(availableCredit) / 100, {
    style: 'currency',
    currency: 'USD',
  });

  const applied = formatNumber(Number(appliedCredit) / 100, {
    style: 'currency',
    currency: 'USD',
  });

  return { applied, available };
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  changeCreditUsage: {
    defaultMessage: 'Change credit usage',
    description: 'Bag | Payment Methods | Change credit usage',
  },
  creditUsageTitle: {
    defaultMessage: 'SG Credit',
    description: 'Bag | Payment Methods | Credit usage title',
  },
  creditUsageHint: {
    defaultMessage: 'Use {applied} of {available}',
    description: 'Bag | Payment Methods | Credit usage hint',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  paymentMethod: {
    gap: theme.spacing['2'],
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing['4'],
    paddingVertical: theme.spacing['3'],
  },
  borderBottom: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.LINEN,
  },
  info: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
  },
  infoText: {
    flex: 1,
    flexWrap: 'wrap',
  },
  spinachText: {
    color: theme.colors.SPINACH,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type CreditPaymentMethodProps = {
  isDisabled: boolean;
  shouldUseCredit: boolean;
  availableCredit: number;
  appliedCredit: number;
  onChangeCreditUsage: (shouldUseCredit: boolean) => void;
  paymentBreakdown: ReturnType<typeof usePaymentBreakdown>;
};
